import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel } from './contexts';
import { must, useDb } from './db';

export type Audience = Database['public']['Enums']['announcement_audience'];
export type NotificationKind = Database['public']['Enums']['notification_kind'];

export interface AnnouncementItem {
  id: string;
  title: string;
  body: string;
  audience: Audience;
  classLabel: string | null;
  requiresAck: boolean;
  publishedAt: string;
  author: string | null;
  /** my own receipt, when I am a recipient */
  readAt: string | null;
  isRecipient: boolean;
  /** for the author and the administration */
  stats?: { recipients: number; read: number };
}

/** Announcements of a school I can see, newest first, with my receipt and (if allowed) stats. */
export function useAnnouncements(schoolId: string, memberIds: ReadonlyArray<string>) {
  const db = useDb();
  return useQuery({
    queryKey: ['announcements', schoolId, memberIds],
    queryFn: async () => {
      const [announcements, receipts, stats] = await Promise.all([
        db
          .from('announcements')
          .select(
            `id, title, body, audience, requires_ack, published_at,
             class:classes!announcements_school_id_class_id_fkey(grade, letter),
             author:school_members!announcements_school_id_author_member_id_fkey(first_name, last_name, middle_name)`,
          )
          .eq('school_id', schoolId)
          .order('published_at', { ascending: false }),
        // only my own receipts: a school-wide announcement has hundreds of rows
        db
          .from('announcement_recipients')
          .select('announcement_id, read_at')
          .eq('school_id', schoolId)
          .in('member_id', [...memberIds]),
        db.rpc('announcement_stats', { p_school: schoolId }),
      ]);
      const mine = must(receipts);
      const counts = must(stats);
      return must(announcements).map((a): AnnouncementItem => {
        const receipt = mine.find((r) => r.announcement_id === a.id);
        const count = counts.find((c) => c.announcement_id === a.id);
        return {
          id: a.id,
          title: a.title,
          body: a.body,
          audience: a.audience,
          classLabel: a.class ? classLabel(a.class) : null,
          requiresAck: a.requires_ack,
          publishedAt: a.published_at,
          author: a.author
            ? `${a.author.last_name} ${a.author.first_name} ${a.author.middle_name ?? ''}`.trim()
            : null,
          readAt: receipt?.read_at ?? null,
          isRecipient: receipt !== undefined,
          ...(count && { stats: { recipients: count.recipients, read: count.read } }),
        };
      });
    },
  });
}

export function useAnnouncementActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['announcements'] });
  return {
    publish: useMutation({
      mutationFn: async (a: {
        schoolId: string;
        audience: Audience;
        classId: string | null;
        title: string;
        body: string;
        requiresAck: boolean;
      }) =>
        must(
          await db.rpc('publish_announcement', {
            p_school: a.schoolId,
            p_audience: a.audience,
            // the generated type has no null for an argument without a default; SQL takes null
            p_class: a.classId as string,
            p_title: a.title,
            p_body: a.body,
            p_requires_ack: a.requiresAck,
          }),
        ),
      onSettled: invalidate,
    }),
    /** Those who have not read it yet get a reminder (at most once every six hours). */
    remind: useMutation({
      mutationFn: async (announcementId: string) =>
        must(await db.rpc('remind_unread', { p_announcement: announcementId })),
    }),
    markRead: useMutation({
      mutationFn: async (a: { announcementId: string; memberIds: ReadonlyArray<string> }) =>
        must(
          await db
            .from('announcement_recipients')
            .update({ read_at: new Date().toISOString() })
            .eq('announcement_id', a.announcementId)
            .in('member_id', [...a.memberIds])
            .is('read_at', null),
        ),
      onSettled: invalidate,
    }),
  };
}

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  link: Record<string, string>;
  createdAt: string;
  readAt: string | null;
}

export function useNotifications(memberIds: ReadonlyArray<string>) {
  const db = useDb();
  return useQuery({
    queryKey: ['notifications', memberIds],
    enabled: memberIds.length > 0,
    refetchInterval: 60_000,
    queryFn: async () =>
      must(
        await db
          .from('notifications')
          .select('id, kind, title, body, link, created_at, read_at')
          .in('member_id', [...memberIds])
          .order('created_at', { ascending: false })
          .limit(100),
      ).map((n): NotificationItem => ({
        id: n.id,
        kind: n.kind,
        title: n.title,
        body: n.body,
        link: n.link as Record<string, string>,
        createdAt: n.created_at,
        readAt: n.read_at,
      })),
  });
}

export function useMarkNotificationsRead() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ids: ReadonlyArray<string>) =>
      must(
        await db
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .in('id', [...ids])
          .is('read_at', null),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
